/**
 * blockDefinitions: the single canonical source of truth for all 16 content
 * block types.
 *
 * Each definition bundles the descriptor metadata (label, icon, etc.), the
 * display component (static import — safe for the display bundle), the `isWide`
 * layout flag, and the default-data factory. Editor form references live in
 * blockEditorRegistry.js rather than here so that no dynamic import() expressions
 * appear in this module: @babel/runtime@7.28.6 routes dynamic-import helpers
 * through its ESM exports, which Jest 29's resolver cannot handle in CJS mode.
 *
 * The display/editor bundle separation is maintained by module boundaries:
 * the display path (blockRegistry.js → blockDefinitions.js) never imports
 * blockEditorRegistry.js, so editor-only code (TipTap, etc.) never reaches
 * the renderer bundle regardless of import style.
 *
 * The three derived consumers each strip what they need:
 *   - blockRegistry.js       → { type: displayComponent }
 *   - blockEditorRegistry.js → { type: editorFormComponent } (static imports)
 *   - blockTypes.js          → descriptor fields only (BLOCK_TYPE_LIST)
 *
 * NOTE: The server's BLOCK_TYPES enum in
 * server/src/api/ecosystem-content/ecosystem-content.model.js is intentionally
 * kept manual — it runs in a separate Node runtime and cannot import client
 * code. When adding a block type here, update the server enum and its spec
 * as a separate, conscious step.
 */

// Display components — static imports are fine; they are on the renderer path.
import HeroBlock from './blocks/HeroBlock';
import HeadingBlock from './blocks/HeadingBlock';
import ParagraphBlock from './blocks/ParagraphBlock';
import ImageBlock from './blocks/ImageBlock';
import ImageGridBlock from './blocks/ImageGridBlock';
import StatBlock from './blocks/StatBlock';
import QuoteBlock from './blocks/QuoteBlock';
import CtaButtonBlock from './blocks/CtaButtonBlock';
import EcosystemOrgsBlock from './blocks/EcosystemOrgsBlock';
import EcosystemStatsBlock from './blocks/EcosystemStatsBlock';
import GraphPreviewBlock from './blocks/GraphPreviewBlock';
import CapabilityCardsBlock from './blocks/CapabilityCardsBlock';
import LiveStatsBlock from './blocks/LiveStatsBlock';
import LogoTextBlock from './blocks/LogoTextBlock';
import EcosystemJoinBlock from './blocks/EcosystemJoinBlock';
import RecentOrgsBlock from './blocks/RecentOrgsBlock';

/**
 * Full ordered list of block definitions. Order drives the add-block picker.
 *
 * `isWide` marks types that always render in the wide column. Paragraph's
 * content-based widening (list detection) lives in EcosystemContentRenderer
 * and is unaffected by this flag.
 *
 * @type {Array<{
 *   type: string,
 *   label: string,
 *   description: string,
 *   icon: string,
 *   category: 'authored'|'dynamic',
 *   isWide: boolean,
 *   createDefaultData: function(): object,
 *   displayComponent: React.ComponentType,
 * }>}
 */
const BLOCK_DEFINITIONS = [
    {
        type: 'hero',
        label: 'Hero Banner',
        description:
            'Full-width banner with a heading and optional call-to-action.',
        icon: '🎯',
        category: 'authored',
        isWide: false,
        createDefaultData: () => ({
            heading: '',
            subheading: '',
            backgroundImageUrl: '',
            ctaText: '',
            ctaLink: '',
            secondaryCtaText: '',
            secondaryCtaLink: '',
            audienceItems: [],
            showGraphPreview: false,
        }),
        displayComponent: HeroBlock,
    },
    {
        type: 'heading',
        label: 'Section Heading',
        description: 'A section heading (level 2 or 3).',
        icon: '🔠',
        category: 'authored',
        isWide: false,
        createDefaultData: () => ({ text: '', level: 2 }),
        displayComponent: HeadingBlock,
    },
    {
        type: 'paragraph',
        label: 'Rich Text',
        description:
            'A formatted paragraph with bold, italic, links, and lists.',
        icon: '📝',
        category: 'authored',
        isWide: false,
        createDefaultData: () => ({ html: '' }),
        displayComponent: ParagraphBlock,
    },
    {
        type: 'image',
        label: 'Image',
        description: 'A single image with alt text and an optional caption.',
        icon: '🖼️',
        category: 'authored',
        isWide: false,
        createDefaultData: () => ({ url: '', alt: '', caption: '' }),
        displayComponent: ImageBlock,
    },
    {
        type: 'image-grid',
        label: 'Image Grid',
        description: 'A responsive grid of multiple images.',
        icon: '🗂️',
        category: 'authored',
        isWide: true,
        createDefaultData: () => ({ images: [] }),
        displayComponent: ImageGridBlock,
    },
    {
        type: 'stat',
        label: 'Custom Stats',
        description: 'A row of authored value + label statistic figures.',
        icon: '📊',
        category: 'authored',
        isWide: true,
        createDefaultData: () => ({ items: [] }),
        displayComponent: StatBlock,
    },
    {
        type: 'quote',
        label: 'Pull Quote',
        description: 'A highlighted quote with an optional attribution.',
        icon: '❝',
        category: 'authored',
        isWide: false,
        createDefaultData: () => ({
            text: '',
            attribution: '',
            attributionUrl: '',
        }),
        displayComponent: QuoteBlock,
    },
    {
        type: 'cta-button',
        label: 'Call-to-Action Button',
        description: 'A standalone button linking somewhere.',
        icon: '🔘',
        category: 'authored',
        isWide: false,
        createDefaultData: () => ({ text: '', link: '', variant: 'primary' }),
        displayComponent: CtaButtonBlock,
    },
    {
        type: 'logo-text',
        label: 'Image & Text',
        description:
            'Two-column block: image on one side, heading + body + optional CTA on the other. Sides can be flipped.',
        icon: '🖼',
        category: 'authored',
        isWide: true,
        createDefaultData: () => ({
            imageUrl: '',
            imageAlt: '',
            heading: '',
            body: '',
            buttonText: '',
            buttonLink: '',
            reversed: false,
        }),
        displayComponent: LogoTextBlock,
    },
    {
        type: 'ecosystem-join',
        label: 'Call-to-Action Section',
        description:
            'Dark CTA section with a heading, body, and up to 4 action buttons.',
        icon: '🚀',
        category: 'authored',
        isWide: false,
        createDefaultData: () => ({
            heading: 'Get Involved',
            body: '',
            buttons: [
                {
                    text: 'Create an Account',
                    link: '/signup',
                    variant: 'accent',
                },
            ],
        }),
        displayComponent: EcosystemJoinBlock,
    },
    {
        type: 'ecosystem-orgs',
        label: 'Organizations in this Ecosystem',
        description:
            'Live carousel of a random cross-section of approved organizations.',
        icon: '🏢',
        category: 'dynamic',
        isWide: false,
        createDefaultData: () => ({ limit: 9 }),
        displayComponent: EcosystemOrgsBlock,
    },
    {
        type: 'ecosystem-stats',
        label: 'Ecosystem Stats',
        description:
            'Live ecosystem metrics (organizations, connections). Each section (stats, heatmap, charts, growth areas) can be shown or hidden.',
        icon: '📈',
        category: 'dynamic',
        isWide: true,
        createDefaultData: () => ({ hiddenSections: [] }),
        displayComponent: EcosystemStatsBlock,
    },
    {
        type: 'live-stats',
        label: 'Live Stats',
        description:
            'Live platform statistics (organizations, connections, sectors, etc.) with configurable heading and stat selection.',
        icon: '📡',
        category: 'dynamic',
        isWide: true,
        createDefaultData: () => ({
            sectionHeading: 'A Growing and Thriving Ecosystem',
            visibleStats: ['organizations', 'connections'],
        }),
        displayComponent: LiveStatsBlock,
    },
    {
        type: 'graph-preview',
        label: 'Network Visualization Preview',
        description: 'Live preview of the ecosystem network visualization.',
        icon: '🕸️',
        category: 'dynamic',
        isWide: false,
        createDefaultData: () => ({}),
        displayComponent: GraphPreviewBlock,
    },
    {
        type: 'capability-cards',
        label: 'Capability Cards',
        description:
            'Feature-flag-aware grid of platform capability cards. Cards auto-show/hide based on enabled features.',
        icon: '🃏',
        category: 'dynamic',
        isWide: true,
        createDefaultData: () => ({
            sectionHeading: 'What You Can Do Here',
            hiddenCards: [],
        }),
        displayComponent: CapabilityCardsBlock,
    },
    {
        type: 'recent-orgs',
        label: 'Recently Added Organizations',
        description:
            'Live carousel of the newest approved organizations, sorted by join date. Auto-updates without curation.',
        icon: '🆕',
        category: 'dynamic',
        isWide: false,
        createDefaultData: () => ({ limit: 9 }),
        displayComponent: RecentOrgsBlock,
    },
];

export default BLOCK_DEFINITIONS;
